import { z } from "zod";
import { firestoreTimestampSchema, documentTypeEnum } from "./common.schema";

// ============================================
// DOCUMENT ENTITY SCHEMA
// ============================================

export const documentSchema = z.object({
  id: z.string(),
  url: z.string(),
  storagePath: z.string().optional(),
  filename: z.string(),
  mimeType: z.string(),
  size: z.number(),
  senderId: z.string(),
  recipientIds: z.array(z.string()),
  visibleToCompanyIds: z.array(z.string()),
  notes: z.string().optional(),
  sendEmail: z.boolean(),
  type: documentTypeEnum.optional(),
  readConfirmation: z
    .record(z.object({ readAt: z.string().optional() }))
    .optional(),
  createdAt: firestoreTimestampSchema.optional(),
  updatedAt: firestoreTimestampSchema.optional(),
  patientId: z.string().optional(),
  appointmentId: z.string().optional(),
  companyId: z.string().optional(),
  agendaDate: z.string().optional(),
});

export type Document = z.infer<typeof documentSchema>;

// ============================================
// CREATE DOCUMENT DTO
// ============================================

export const createDocumentSchema = z.object({
  recipientIds: z.array(z.string()).optional(), // Make optional to allow daily agenda documents to be created without specific recipients
  notes: z.string().optional(),
  sendEmail: z.boolean().optional(),
  type: documentTypeEnum.optional(),
  patientId: z.string().optional(),
  appointmentId: z.string().optional(),
  companyId: z.string().optional(),
  agendaDate: z.string().optional(),
});

export type CreateDocument = z.infer<typeof createDocumentSchema>;

// ============================================
// UPDATE DOCUMENT DTO
// ============================================

export const updateDocumentSchema = z.object({
  notes: z.string().optional(),
  recipientIds: z.array(z.string()).optional(),
  sendEmail: z.boolean().optional(),
  type: documentTypeEnum.optional(),
  patientId: z.string().optional(),
  appointmentId: z.string().optional(),
  companyId: z.string().optional(),
  agendaDate: z.string().optional(),
});

export type UpdateDocument = z.infer<typeof updateDocumentSchema>;

// ============================================
// MARK AS READ DTO
// ============================================

export const markAsReadSchema = z.object({
  readerId: z.string(),
});

export type MarkAsRead = z.infer<typeof markAsReadSchema>;

// ============================================
// BULK IDONEITA UPLOAD DTOs
// ============================================

/**
 * Single file association data for bulk idoneita upload
 * Each item represents one file with its associations to patient, company, and appointment
 */
export const bulkIdoneitaItemSchema = z.object({
  /** Index of the file in the upload array (for matching frontend files to backend processing) */
  fileIndex: z.number(),
  /** Original filename */
  filename: z.string(),
  /** Patient ID to associate */
  patientId: z.string().optional(),
  /** Patient name (for display/verification) */
  patientName: z.string().optional(),
  /** Company ID to associate */
  companyId: z.string().optional(),
  /** Company name (for display/verification) */
  companyName: z.string().optional(),
  /** Appointment ID to associate */
  appointmentId: z.string().optional(),
  /** Appointment date (for display/verification) */
  appointmentDate: z.string().optional(),
  /** Notes for this document */
  notes: z.string().optional(),
});

export type BulkIdoneitaItem = z.infer<typeof bulkIdoneitaItemSchema>;

/**
 * Request body for bulk idoneita upload
 * Contains array of file associations
 */
export const createBulkIdoneitaSchema = z.object({
  /** Array of file associations */
  items: z
    .array(bulkIdoneitaItemSchema)
    .min(1, "At least one file is required"),
  /** Whether to send email notifications to companies */
  sendEmail: z.boolean().optional().default(true),
  /** Whether to also send email notifications to each patient */
  sendEmailToPatient: z.boolean().optional().default(false),
});

export type CreateBulkIdoneita = z.infer<typeof createBulkIdoneitaSchema>;

/**
 * Response for parsing filename to extract patient info
 */
export const parseFilenameResponseSchema = z.object({
  /** Parsed last name (COGNOME) */
  lastName: z.string(),
  /** Parsed first name (NOME) */
  firstName: z.string(),
  /** Additional part of filename */
  additional: z.string().optional(),
  /** Found patient matches */
  patients: z.array(
    z.object({
      id: z.string(),
      firstName: z.string(),
      lastName: z.string(),
      companyId: z.string(),
      companyName: z.string().optional(),
    }),
  ),
  /** Found appointment for patient (most recent or upcoming idoneita) */
  appointment: z
    .object({
      id: z.string(),
      date: z.string(),
      time: z.string(),
      patientId: z.string(),
      companyId: z.string(),
    })
    .optional(),
});

export type ParseFilenameResponse = z.infer<typeof parseFilenameResponseSchema>;
